import { type FC, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { useIntersectionObserver } from 'usehooks-ts'

import {
  type UserOrderResponseV2,
  type UserOrderResponseWithFlags,
  type UserSubOrderV2,
} from '@redteclab/api/clients/bully'

import {
  OrderDetailsProductImageList,
  OrderSubOrderStatus,
  userSubOrderAccessorGetSubOrderProducts,
} from '../../../order'
import { priceGetFormattedPrice } from '../../../price'

import { useContactPageLastOrderContext } from './ContactPageLastOrderContext'

export const ContactPageSubOrderDetailsCard: FC<{
  href: string
  onClick?: () => void
  order: UserOrderResponseV2 | UserOrderResponseWithFlags
  subOrder: UserSubOrderV2
}> = ({ href, onClick, order, subOrder }) => {
  const intl = useIntl()

  const { activeSubOrder, setActiveSubOrder } = useContactPageLastOrderContext()

  const { sellerName, subOrderId, totalAmount } = subOrder

  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 0.5,
  })

  useEffect(() => {
    // Set active suborder when in view
    if (
      isIntersecting &&
      setActiveSubOrder &&
      subOrderId !== activeSubOrder?.subOrderId
    ) {
      setActiveSubOrder(subOrder)
    }
  }, [isIntersecting, setActiveSubOrder, subOrder, subOrderId, activeSubOrder])

  if (!subOrderId || !order.orderMasterData?.orderNumber) {
    return null
  }

  const subOrderProducts = userSubOrderAccessorGetSubOrderProducts(subOrder)

  return (
    <a
      className="flex h-full flex-col rounded-m bg-light-primary-low p-4 shadow-m transition-all hover:bg-light-brand-medium hover:shadow-xl"
      href={href}
      onClick={onClick}
      ref={ref}
    >
      <div className="flex w-full justify-between">
        <span className="line-clamp-2 text-start text-m font-medium text-dark-primary-max">
          {sellerName}
        </span>
        <span
          className="text-center"
          data-qa-id="direct-contact-form-order-total-amount"
        >
          {priceGetFormattedPrice(totalAmount ?? 0, intl)}
        </span>
      </div>
      <div className="flex justify-between gap-2">
        <OrderSubOrderStatus includeNowIndicator subOrder={subOrder} />
      </div>
      <div className="mt-auto flex flex-row items-center justify-between">
        <OrderDetailsProductImageList products={subOrderProducts} />
      </div>
    </a>
  )
}
